import { buildWallOverlapBSPs } from "building-generation/elements/roofs/roofWalls";
import { makeGableEndWallFraming } from "building-generation/elements/roofWallFraming";
import { roofsOnLevel } from "building-generation/elements/walls/roofsOnLevel";
import type { Block } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { HeaderAssemblyEntity } from "building-types/framing/headerAssemblyTypes";
import type { StandardLumberCounterSet } from "building-types/framing/lumberTypes";
import type { TargetedOpeningSettings } from "building-types/openings/opening";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { HeaderFramingSettings } from "building-types/settings/headerFramingSettings";
import type { WallCatalogSettings } from "building-types/settings/wallCatalogSettings";
import type { WallFramingSettings } from "building-types/settings/wallFramingSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import type { WallFraming } from "building-types/walls/wallFramingTypes";
import type { RWTable, Table } from "common/data/table";

export interface MakeBuildingGableEndWallFramingParams
  extends Pick<Building, "levels" | "walls" | "planarGraphPerimeters" | "roofSystems"> {
  readonly roofSystems: Table<GeneratedRoofSystem>;
  readonly blocks: Table<Block>;
  readonly wallSettings: WallSettings;
  readonly lumberCounters?: StandardLumberCounterSet;
  readonly targetedOpeningSettings: TargetedOpeningSettings;
  readonly wallFramingSettings: WallFramingSettings;
  readonly wallCatalogSettings: WallCatalogSettings;
  readonly headerFramingSettings: HeaderFramingSettings;
  readonly headerAssemblies: Table<HeaderAssemblyEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
}

export function makeBuildingGableEndWallFraming({
  levels,
  walls,
  wallFramingSettings,
  roofSystems,
  blocks,
  wallSettings,
  targetedOpeningSettings,
  headerFramingSettings,
  headerAssemblies,
  framingComponents,
}: MakeBuildingGableEndWallFramingParams): Table<WallFraming[]> {
  const gableEndWallFramingSystems: RWTable<WallFraming[]> = {};

  // level-invariant: built from the full wall/block tables, so share it across levels
  const wallOverlapBSPs = buildWallOverlapBSPs(walls, blocks, null, true);

  for (const levelId in levels) {
    gableEndWallFramingSystems[levelId] = makeGableEndWallFraming({
      roofs: roofsOnLevel(levelId, roofSystems),
      wallOverlapBSPs,
      wallFramingSettings,
      wallSettings,
      targetedOpeningSettings,
      headerFramingSettings,
      headerAssemblies,
      framingComponents,
    });
  }

  return gableEndWallFramingSystems;
}
